import { css, html, LitElement, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceCart,
    type CommerceDataSource,
    type CommerceEventUnsubscribe,
    type CommerceProductComponent,
} from "@teqtank/commerce-sdk";
import "../share/teqtank-share-panel.js";

@customElement("teqtank-cart-toolbox")
export class TeqtankCartToolbox extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" }) public accessor dataSource: CommerceDataSource =
        "playground";
    @property({ attribute: "checkout-route" }) public accessor checkoutRoute = "/checkout";
    @property({ attribute: "enrollment-route" }) public accessor enrollmentRoute = "/enrollment";
    @property() public accessor label = "Bag";
    @property() public accessor heading = "Shopping bag";
    @property() public accessor eyebrow = "Your ritual";
    @property({ attribute: "share-label" }) public accessor shareLabel = "Share this cart";
    @property({ attribute: "show-share", type: Boolean }) public accessor showShare = true;
    @property({ attribute: "open-on-add", type: Boolean }) public accessor openOnAdd = false;

    @state() private accessor cart: CommerceCart | undefined;
    @state() private accessor loading = true;
    @state() private accessor busyLineId = "";
    @state() private accessor errorMessage = "";
    @state() private accessor shareCartOpen = false;
    @state() private accessor restartingEnrollment = false;

    @query("dialog") private accessor dialog!: HTMLDialogElement;
    @query(".trigger") private accessor trigger!: HTMLButtonElement;

    private ownedCommerce: Commerce | undefined;
    private boundCommerce: Commerce | undefined;
    private bindVersion = 0;
    private unsubscribe: CommerceEventUnsubscribe | undefined;
    private unsubscribeMarket: CommerceEventUnsubscribe | undefined;
    private unsubscribeEnrollment: CommerceEventUnsubscribe | undefined;
    private unsubscribeCheckout: CommerceEventUnsubscribe | undefined;

    public static override styles = css`
        :host {
            --tt-ink: var(--teqtank-color-text, #16221d);
            --tt-muted: var(--teqtank-color-text-muted, #6b746e);
            --tt-accent: var(--teqtank-color-accent, #728f4f);
            --tt-accent-contrast: var(--teqtank-color-on-accent, #fff);
            --tt-surface: var(--teqtank-color-surface, #faf8f4);
            --tt-soft: var(--teqtank-color-surface-soft, #eee8df);
            --tt-line: var(--teqtank-color-border, rgba(22, 34, 29, 0.12));
            --tt-display: var(--teqtank-font-heading, Georgia, serif);
            --tt-body: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            display: inline-block;
            color: var(--tt-ink);
            font-family: var(--tt-body);
        }

        * {
            box-sizing: border-box;
        }
        button,
        a {
            font: inherit;
        }
        button {
            cursor: pointer;
        }

        .trigger {
            display: inline-flex;
            align-items: center;
            gap: 0.45rem;
            min-height: 2.75rem;
            padding: 0.65rem 0.9rem;
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font-weight: 800;
        }

        .count {
            display: inline-grid;
            place-items: center;
            min-width: 1.45rem;
            min-height: 1.45rem;
            padding: 0 0.28rem;
            border-radius: 999px;
            background: var(--tt-accent);
            color: var(--tt-accent-contrast);
            font-size: 0.75rem;
        }

        dialog {
            width: min(var(--teqtank-cart-toolbox-width, 30rem), 100%);
            max-width: none;
            height: 100dvh;
            max-height: none;
            margin: 0 0 0 auto;
            padding: 0;
            border: 0;
            background: var(--teqtank-cart-toolbox-background, var(--tt-surface));
            color: var(--tt-ink);
            box-shadow: -1rem 0 3rem rgb(0 0 0 / 18%);
            transform: translateX(100%);
            transition: transform 220ms ease;
        }

        dialog[open] {
            transform: translateX(0);
        }
        dialog.closing {
            transform: translateX(100%);
        }
        dialog::backdrop {
            background: rgb(0 0 0 / 42%);
            transition: background 220ms ease;
        }
        dialog.closing::backdrop {
            background: rgb(0 0 0 / 0%);
        }

        @starting-style {
            dialog[open] {
                transform: translateX(100%);
            }
        }

        .drawer {
            display: grid;
            grid-template-rows: auto minmax(0, 1fr) auto;
            height: 100%;
            min-width: 0;
            container-type: inline-size;
        }

        .head {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 1rem;
            padding: 1.5rem;
            border-bottom: 1px solid var(--tt-line);
        }

        .head small {
            color: var(--tt-muted);
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.1em;
        }

        .head h2 {
            margin: 0.2rem 0 0;
            font: 500 clamp(2rem, 7vw, 2.8rem)/1 var(--tt-display);
        }

        .close {
            display: grid;
            place-items: center;
            width: 2.75rem;
            height: 2.75rem;
            border: 0;
            border-radius: 50%;
            background: transparent;
            color: var(--tt-ink);
            font-size: 2rem;
            line-height: 1;
        }

        .close:hover {
            background: var(--tt-soft);
        }

        .lines {
            min-height: 0;
            padding: 0 1.5rem;
            overflow: auto;
            overscroll-behavior: contain;
        }

        .line {
            display: grid;
            grid-template-columns: 72px minmax(0, 1fr) auto;
            gap: 0.8rem;
            padding: 1rem 0;
            border-bottom: 1px solid var(--tt-line);
        }

        .image,
        .placeholder {
            width: 72px;
            height: 84px;
            object-fit: var(--teqtank-product-image-fit, contain);
            background: var(--tt-soft);
        }

        .placeholder {
            display: grid;
            place-items: center;
            padding: 0.35rem;
            color: var(--tt-accent);
            text-align: center;
            font: 500 0.7rem var(--tt-display);
        }

        .copy {
            min-width: 0;
        }
        .copy h3 {
            margin: 0.1rem 0 0.35rem;
            font-size: 0.9rem;
        }
        .copy small {
            display: block;
            color: var(--tt-muted);
            font-size: 0.7rem;
        }
        .components {
            margin: 0.55rem 0 0;
            padding-left: 1rem;
            color: var(--tt-muted);
            font-size: 0.7rem;
        }
        .components li {
            margin-top: 0.15rem;
        }
        .components .components {
            margin-top: 0.15rem;
        }

        .quantity {
            display: flex;
            align-items: center;
            gap: 0.55rem;
            margin-top: 0.65rem;
        }

        .quantity button {
            width: 1.75rem;
            height: 1.75rem;
            border: 1px solid var(--tt-line);
            background: transparent;
            color: var(--tt-ink);
        }

        .quantity strong {
            min-width: 1.25rem;
            text-align: center;
            font-size: 0.78rem;
        }

        .quantity.locked {
            gap: 0.35rem;
            color: var(--tt-muted);
            font-size: 0.72rem;
        }

        .lock-note {
            margin: 0.6rem 0 0;
            padding: 0.55rem 0.65rem;
            border-radius: 0.55rem;
            background: color-mix(in srgb, var(--tt-accent) 8%, var(--tt-surface));
            color: var(--tt-muted);
            font-size: 0.7rem;
            line-height: 1.4;
        }

        .remove {
            margin-top: 0.55rem;
            padding: 0;
            border: 0;
            background: transparent;
            color: var(--tt-muted);
            font-size: 0.68rem;
            text-decoration: underline;
        }

        .line-total {
            align-self: start;
            white-space: nowrap;
            font-size: 0.8rem;
        }
        button:disabled {
            cursor: wait;
            opacity: 0.6;
        }

        .empty,
        .loading,
        .error {
            padding: 2rem 0;
            color: var(--tt-muted);
        }
        .error {
            color: #8b2424;
        }

        .summary {
            padding: 1.25rem 1.5rem 1.5rem;
            border-top: 1px solid var(--tt-line);
            background: var(--tt-surface);
        }

        .summary-row {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.4rem;
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .summary-row.total {
            margin-top: 0.75rem;
            padding-top: 0.8rem;
            border-top: 1px solid var(--tt-line);
            color: var(--tt-ink);
            font-size: 1rem;
            font-weight: 800;
        }

        .actions {
            display: grid;
            gap: 0.7rem;
            margin-top: 1rem;
        }

        .enrollment-management {
            margin-top: 1rem;
            padding: 0.9rem;
            border: 1px solid color-mix(in srgb, var(--tt-accent) 24%, var(--tt-line));
            border-radius: 0.85rem;
            background: color-mix(in srgb, var(--tt-accent) 6%, var(--tt-surface));
        }

        .enrollment-management strong,
        .enrollment-management span {
            display: block;
        }

        .enrollment-management span {
            margin-top: 0.25rem;
            color: var(--tt-muted);
            font-size: 0.72rem;
            line-height: 1.45;
        }

        .restart-enrollment {
            width: 100%;
            min-height: 2.65rem;
            margin-top: 0.75rem;
            border: 1px solid var(--tt-accent);
            border-radius: 999px;
            background: transparent;
            color: var(--tt-ink);
            font-weight: 800;
        }

        .checkout {
            display: flex;
            justify-content: center;
            padding: 0.95rem 1rem;
            border: 1px solid var(--tt-ink);
            border-radius: 999px;
            background: var(--tt-ink);
            color: #fff;
            font-weight: 800;
            text-decoration: none;
        }

        .share-trigger {
            width: 100%;
            min-height: 2.75rem;
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font-weight: 800;
        }

        .share-backdrop {
            position: fixed;
            inset: 0;
            z-index: 2;
            border: 0;
            background: rgb(0 0 0 / 42%);
        }

        .share-panel {
            position: fixed;
            inset: 0 0 0 auto;
            z-index: 3;
            width: min(460px, 100%);
            box-sizing: border-box;
            padding: clamp(20px, 5vw, 36px);
            overflow: auto;
            background: var(--tt-surface);
            color: var(--tt-ink);
            box-shadow: -18px 0 50px rgb(0 0 0 / 18%);
        }

        .share-head {
            margin-bottom: 18px;
            display: flex;
            justify-content: space-between;
            gap: 16px;
            align-items: start;
        }

        .share-head small {
            color: var(--tt-muted);
            font-size: 0.7rem;
            letter-spacing: 0.1em;
        }

        .share-head h2 {
            margin: 4px 0 0;
            font: 500 28px var(--tt-display);
        }
        .share-close {
            border: 0;
            background: transparent;
            color: inherit;
            font-size: 28px;
        }

        .share-preview {
            margin-bottom: 16px;
            padding: 16px;
            display: flex;
            gap: 12px;
            align-items: center;
            border-radius: 14px;
            background: var(--tt-soft);
        }

        .share-images {
            display: flex;
            flex: 0 0 auto;
        }
        .share-images img,
        .share-images span {
            width: 46px;
            height: 54px;
            margin-left: -10px;
            border: 2px solid var(--tt-soft);
            border-radius: 8px;
            object-fit: cover;
            background: #fff;
        }
        .share-images > :first-child {
            margin-left: 0;
        }

        @media (prefers-reduced-motion: reduce) {
            dialog {
                transition: none;
            }
        }

        @container (max-width: 420px) {
            .head,
            .summary {
                padding-inline: 1rem;
            }
            .lines {
                padding-inline: 1rem;
            }
            .line {
                grid-template-columns: 64px minmax(0, 1fr);
            }
            .image,
            .placeholder {
                width: 64px;
                height: 76px;
            }
            .line-total {
                grid-column: 2;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        void this.bind();
    }

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce") && this.commerce !== this.boundCommerce) {
            if (this.ownedCommerce !== undefined && this.commerce !== this.ownedCommerce) {
                this.ownedCommerce.dispose();
                this.ownedCommerce = undefined;
            }
            void this.bind();
        }
    }

    public override disconnectedCallback(): void {
        this.bindVersion += 1;
        this.unsubscribe?.();
        this.unsubscribeMarket?.();
        this.unsubscribeEnrollment?.();
        this.unsubscribeCheckout?.();
        const ownedCommerce = this.ownedCommerce;
        if (this.commerce === ownedCommerce) {
            this.boundCommerce = undefined;
            this.commerce = undefined;
        }
        ownedCommerce?.dispose();
        this.ownedCommerce = undefined;
        super.disconnectedCallback();
    }

    public async refresh(): Promise<CommerceCart> {
        const runtime = await this.requireCommerce();
        const cart = await runtime.cart.get();
        this.cart = cart;
        return cart;
    }

    public open(): void {
        if (!this.dialog?.open) {
            this.dialog?.showModal();
            this.requestUpdate();
            this.dispatchEvent(
                new CustomEvent("teqtank-cart-toolbox-opened", { bubbles: true, composed: true }),
            );
        }
    }

    public close(): void {
        const dialog = this.dialog;
        if (!dialog?.open || dialog.classList.contains("closing")) return;

        if (globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            dialog.close();
            return;
        }

        dialog.classList.add("closing");
        globalThis.setTimeout(() => {
            if (dialog.open) dialog.close();
            dialog.classList.remove("closing");
        }, 220);
    }

    public toggle(): void {
        if (this.dialog?.open) this.close();
        else this.open();
    }

    protected override render() {
        const cart = this.cart;
        const count = cart?.totals.itemCount ?? 0;

        return html`
            <button
                class="trigger"
                part="trigger"
                type="button"
                aria-haspopup="dialog"
                aria-expanded=${this.dialog?.open ? "true" : "false"}
                aria-label=${`${this.label}, ${count} ${count === 1 ? "item" : "items"}`}
                @click=${this.open}
            >
                <span part="trigger-label">${this.label}</span
                ><span class="count" part="count" aria-hidden="true">${count}</span>
            </button>

            <dialog
                part="dialog"
                aria-labelledby="cart-toolbox-heading"
                @cancel=${this.handleCancel}
                @close=${this.handleClosed}
                @click=${this.handleDialogClick}
            >
                <section class="drawer" part="drawer">
                    <header class="head" part="header">
                        <div>
                            <small part="eyebrow">${this.eyebrow}</small>
                            <h2 part="title" id="cart-toolbox-heading">${this.heading}</h2>
                        </div>
                        <button
                            class="close"
                            part="close"
                            type="button"
                            aria-label="Close ${this.label.toLowerCase()}"
                            @click=${this.close}
                        >
                            ×
                        </button>
                    </header>

                    <div class="lines" part="item-list" aria-live="polite">
                        ${this.renderLines(cart)}
                    </div>

                    ${cart === undefined ? nothing : this.renderSummary(cart)}
                </section>
                ${this.shareCartOpen && cart !== undefined ? this.renderSharePanel(cart) : nothing}
            </dialog>
        `;
    }

    private renderLines(cart: CommerceCart | undefined) {
        if (this.loading)
            return html`<p class="loading" part="loading-state">Loading your bag…</p>`;
        if (this.errorMessage)
            return html`<p class="error" part="error-state" role="alert">${this.errorMessage}</p>`;
        if (cart === undefined || cart.lines.length === 0)
            return html`<p class="empty" part="empty-state">
                Your bag is ready for a new ritual.
            </p>`;

        return cart.lines.map((line) => {
            const enrollmentLine = this.enrollmentLine(line.id);
            const enrollmentMetadataPending = this.enrollmentMetadataPending;
            const quantityLocked = enrollmentMetadataPending || enrollmentLine?.quantityLocked;
            const removalLocked = enrollmentMetadataPending || enrollmentLine?.removalLocked;
            return html`
                <article class="line" part="item">
                    ${
                        line.image === null
                            ? html`<div class="placeholder">${line.name}</div>`
                            : html`<img
                                  class="image"
                                  src=${line.image.url}
                                  alt=${line.image.alternativeText}
                              />`
                    }
                    <div class="copy" part="item-content">
                        <h3 part="item-title">${line.name}</h3>
                        <small
                            >${line.purchaseType === "recurring" ? `Subscribe & save${line.frequency?.description === undefined ? (line.frequencyDays === undefined ? "" : ` · every ${line.frequencyDays} days`) : ` · ${line.frequency.description}`}` : "One-time purchase"}</small
                        >
                        <small>${line.unitPrice.formatted} each</small>
                        ${this.renderComponents(line.components)}
                        ${
                            quantityLocked
                                ? html`<div class="quantity locked" part="quantity locked-quantity">
                                      <span>Quantity</span><strong>${line.quantity}</strong>
                                  </div>`
                                : html`<div
                                      class="quantity"
                                      part="quantity"
                                      aria-label=${`Quantity for ${line.name}`}
                                  >
                                      <button
                                          type="button"
                                          aria-label=${`Decrease ${line.name} quantity`}
                                          ?disabled=${this.busyLineId === line.id}
                                          @click=${() => this.changeQuantity(line.id, line.quantity - 1)}
                                      >
                                          −
                                      </button>
                                      <strong aria-live="polite">${line.quantity}</strong>
                                      <button
                                          type="button"
                                          aria-label=${`Increase ${line.name} quantity`}
                                          ?disabled=${this.busyLineId === line.id}
                                          @click=${() => this.changeQuantity(line.id, line.quantity + 1)}
                                      >
                                          +
                                      </button>
                                  </div>`
                        }
                        ${
                            removalLocked
                                ? html`<p class="lock-note" part="lock-note">
                                      ${
                                          enrollmentMetadataPending
                                              ? "Confirming enrollment item rules…"
                                              : (enrollmentLine?.lockReason ??
                                                "This item is included with your enrollment selection.")
                                      }
                                  </p>`
                                : html`<button
                                      class="remove"
                                      part="remove-action"
                                      type="button"
                                      ?disabled=${this.busyLineId === line.id}
                                      @click=${() => this.removeLine(line.id)}
                                  >
                                      Remove
                                  </button>`
                        }
                    </div>
                    <strong class="line-total" part="item-total"
                        >${line.lineTotal.formatted}</strong
                    >
                </article>
            `;
        });
    }

    private renderComponents(
        components: readonly CommerceProductComponent[] | null | undefined,
    ): TemplateResult | typeof nothing {
        if (!components?.length) return nothing;
        return html`
            <div aria-label="Kit contents">
                <small>Includes</small>
                <ul class="components">
                    ${components.map(
                        (component) => html`
                            <li>
                                ${component.quantity} × ${component.name}
                                ${this.renderComponents(component.components)}
                            </li>
                        `,
                    )}
                </ul>
            </div>
        `;
    }

    private renderSummary(cart: CommerceCart) {
        return html`
            <footer class="summary" part="summary">
                <div class="summary-row">
                    <span>Subtotal</span><strong>${cart.totals.subtotal.formatted}</strong>
                </div>
                ${cart.totals.discount.amount <= 0 ? nothing : html`<div class="summary-row"><span>Discount</span><strong>−${cart.totals.discount.formatted}</strong></div>`}
                <div class="summary-row total" part="summary-total">
                    <span>Total</span><strong>${cart.totals.total.formatted}</strong>
                </div>
                ${
                    this.hasActiveEnrollmentJourney
                        ? html`<div class="enrollment-management" part="enrollment-management">
                              <strong>Enrollment order</strong>
                              <span
                                  >Included items are protected. Start again to choose a different
                                  enrollment pack.</span
                              >
                              <button
                                  class="restart-enrollment"
                                  part="restart-enrollment-action"
                                  type="button"
                                  ?disabled=${this.restartingEnrollment}
                                  @click=${this.restartEnrollment}
                              >
                                  ${
                                      this.restartingEnrollment
                                          ? "Starting again…"
                                          : "Choose a different pack"
                                  }
                              </button>
                          </div>`
                        : nothing
                }
                ${
                    cart.lines.length === 0
                        ? nothing
                        : html`
                              <div class="actions">
                                  <a
                                      class="checkout"
                                      part="primary-action checkout-action"
                                      href=${this.checkoutRoute}
                                      @click=${this.close}
                                      >Continue to checkout</a
                                  >
                                  ${this.showShare ? html`<button class="share-trigger" part="share-action" type="button" @click=${() => (this.shareCartOpen = true)}>${this.shareLabel}</button>` : nothing}
                              </div>
                          `
                }
            </footer>
        `;
    }

    private renderSharePanel(cart: CommerceCart) {
        return html`
            <button
                class="share-backdrop"
                type="button"
                aria-label="Close share cart"
                @click=${() => (this.shareCartOpen = false)}
            ></button>
            <aside
                class="share-panel"
                part="share-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="toolbox-share-title"
            >
                <div class="share-head" part="share-header">
                    <div>
                        <small>SOCIAL COMMERCE</small>
                        <h2 id="toolbox-share-title">Share my cart</h2>
                    </div>
                    <button
                        class="share-close"
                        type="button"
                        aria-label="Close"
                        @click=${() => (this.shareCartOpen = false)}
                    >
                        ×
                    </button>
                </div>
                <div class="share-preview">
                    <div class="share-images">
                        ${cart.lines
                            .slice(0, 4)
                            .map((line) =>
                                line.image === null
                                    ? html`<span></span>`
                                    : html`<img
                                          src=${line.image.url}
                                          alt=${line.image.alternativeText}
                                      />`,
                            )}
                    </div>
                    <div>
                        <strong>${cart.totals.itemCount} item ritual</strong><br /><small
                            >${cart.totals.total.formatted}</small
                        >
                    </div>
                </div>
                <teqtank-share-panel
                    part="share-panel"
                    exportparts="shell:share-shell, primary-action:share-primary-action, qr:share-qr, error-state:share-error-state"
                    .commerce=${this.commerce}
                    type="cart"
                    .targetId=${cart.id}
                    heading="Send this exact cart"
                    description="Create a link that preserves the products, options and recurring choices in this cart."
                    auto-create
                ></teqtank-share-panel>
            </aside>
        `;
    }

    private async bind(): Promise<void> {
        const version = ++this.bindVersion;
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        this.unsubscribeMarket?.();
        this.unsubscribeMarket = undefined;
        this.unsubscribeEnrollment?.();
        this.unsubscribeEnrollment = undefined;
        this.unsubscribeCheckout?.();
        this.unsubscribeCheckout = undefined;
        this.loading = true;
        this.errorMessage = "";

        try {
            const runtime = await this.requireCommerce();
            if (version !== this.bindVersion || !this.isConnected) return;
            this.boundCommerce = runtime;
            this.unsubscribe = runtime.cart.onChanged((event) => {
                this.cart = event.cart;
                this.errorMessage = "";
                if (this.openOnAdd && event.reason === "line-added") this.open();
            });
            this.unsubscribeMarket = runtime.market.onChanged(() => {
                void this.refresh();
            });
            this.unsubscribeEnrollment = runtime.enrollment.onChanged(() => {
                // Enrollment commands mutate the same authoritative cart. Reload it explicitly
                // instead of depending solely on the companion cart event so hosts remain
                // synchronized even when an integration batches or delays that notification.
                void this.refresh();
            });
            this.unsubscribeCheckout = runtime.checkout.onChanged(() => {
                this.requestUpdate();
            });
            this.cart = await runtime.cart.get();
            await runtime.enrollment.restoreIfAvailable();
        } catch (error) {
            if (version !== this.bindVersion) return;
            this.errorMessage =
                error instanceof Error ? error.message : "The Cart could not be loaded.";
        } finally {
            if (version === this.bindVersion) this.loading = false;
        }
    }

    private async changeQuantity(lineId: string, quantity: number): Promise<void> {
        if (quantity <= 0) {
            await this.removeLine(lineId);
            return;
        }

        const runtime = await this.requireCommerce();
        this.busyLineId = lineId;
        this.errorMessage = "";
        try {
            this.cart = await runtime.cart.updateQuantity({ lineId, quantity });
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "The item quantity could not be updated.";
        } finally {
            this.busyLineId = "";
        }
    }

    private async removeLine(lineId: string): Promise<void> {
        const runtime = await this.requireCommerce();
        this.busyLineId = lineId;
        this.errorMessage = "";
        try {
            this.cart = await runtime.cart.remove(lineId);
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "The item could not be removed.";
        } finally {
            this.busyLineId = "";
        }
    }

    private get activeEnrollment() {
        const enrollment = this.boundCommerce?.enrollment.current;
        return enrollment?.status === "active" || enrollment?.status === "checkout"
            ? enrollment
            : null;
    }

    private get hasActiveEnrollmentJourney(): boolean {
        return (
            this.activeEnrollment !== null ||
            this.boundCommerce?.checkout.current?.experience === "enrollment"
        );
    }

    private enrollmentLine(lineId: string) {
        return this.activeEnrollment?.cart.lines.find((value) => value.line.id === lineId);
    }

    private get enrollmentMetadataPending(): boolean {
        return this.hasActiveEnrollmentJourney && this.activeEnrollment === null;
    }

    private readonly restartEnrollment = async (): Promise<void> => {
        const runtime = await this.requireCommerce();
        if (this.restartingEnrollment) return;
        this.restartingEnrollment = true;
        this.errorMessage = "";
        try {
            if (
                !runtime.enrollment.current &&
                runtime.checkout.current?.experience === "enrollment"
            ) {
                await runtime.enrollment.resume();
            }
            if (!runtime.enrollment.current) return;
            await runtime.enrollment.abandon();
            await runtime.enrollment.begin({});
            this.close();
            this.dispatchEvent(
                new CustomEvent("teqtank-enrollment-restarted", {
                    bubbles: true,
                    composed: true,
                }),
            );
            const destination = new URL(this.enrollmentRoute, globalThis.location.href);
            if (
                destination.pathname !== globalThis.location.pathname ||
                destination.search !== globalThis.location.search
            ) {
                globalThis.location.assign(destination.href);
            }
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "Enrollment could not be restarted.";
        } finally {
            this.restartingEnrollment = false;
        }
    };

    private handleCancel = (event: Event): void => {
        event.preventDefault();
        this.close();
    };

    private handleClosed = (): void => {
        this.shareCartOpen = false;
        this.dialog.classList.remove("closing");
        this.trigger?.focus();
        this.requestUpdate();
        this.dispatchEvent(
            new CustomEvent("teqtank-cart-toolbox-closed", { bubbles: true, composed: true }),
        );
    };

    private handleDialogClick = (event: MouseEvent): void => {
        if (event.target === this.dialog) this.close();
    };

    private async requireCommerce(): Promise<Commerce> {
        if (this.commerce !== undefined) return this.commerce;
        const runtime = await TeqtankCommerce.initialize({
            commerceKey: this.commerceKey,
            dataSource: this.dataSource,
        });

        if (this.commerce !== undefined) {
            runtime.dispose();
            return this.commerce;
        }

        this.ownedCommerce = runtime;
        this.boundCommerce = runtime;
        this.commerce = runtime;
        return runtime;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-cart-toolbox": TeqtankCartToolbox;
    }
}
